import { useEffect } from 'react';
import type { UseFormGetValues, UseFormReset, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import type { ProductFeeFormInput } from '../schemas/productFee.schema';
import type { FeeReferenceData } from '../services/productFees';

interface Params {
    mode: 'create' | 'edit';
    refData?: FeeReferenceData;
    defaultValues: ProductFeeFormInput;
    isPerServiceScope: boolean;
    reset: UseFormReset<ProductFeeFormInput>;
    setValue: UseFormSetValue<ProductFeeFormInput>;
    getValues: UseFormGetValues<ProductFeeFormInput>;
    trigger: UseFormTrigger<ProductFeeFormInput>;
    disallowedSchedulingId: string | null;
}

/**
 * Lifecycle effects for the product fee form: resetting a create form, running an initial
 * validation pass for edit rows once reference data loads, forcing "Absorbed" off for
 * per-service scope, and dropping a retired quote type or a disallowed scheduling method.
 */
export function useProductFeeFormEffects({
    mode, refData, defaultValues, isPerServiceScope, reset, setValue, getValues, trigger, disallowedSchedulingId,
}: Params) {
    useEffect(() => {
        if (mode === 'create') {
            reset(defaultValues);
        }
    }, [mode, reset, defaultValues]);

    useEffect(() => {
        if (disallowedSchedulingId && getValues('scheduling_method_id') === disallowedSchedulingId) {
            setValue('scheduling_method_id', '', { shouldValidate: true });
        }
    }, [disallowedSchedulingId, getValues, setValue]);

    // RHF does not run an initial validation pass on its own, so without this an
    // already-valid edit row would show a disabled submit button until the first
    // field interaction. Re-runs once reference data loads, since the resolver
    // (below) depends on it to pick the right schema. Edit-mode only: running this
    // on a pristine create form would populate formState.errors before the user has
    // touched anything.
    useEffect(() => {
        if (mode !== 'edit') return;
        void trigger();
    }, [mode, refData, trigger]);

    useEffect(() => {
        if (isPerServiceScope) {
            setValue('is_absorbed', false);
        }
    }, [isPerServiceScope, setValue]);

    // An edit row can carry a quote type that has since been retired: the reference data
    // only lists active types, so it renders no checkbox, yet the API rejects inactive ids.
    // Drop it here so the selection shown is exactly what will be saved, instead of an
    // unrelated edit failing with a 422 on a value the user cannot see.
    const quoteTypeOptions = refData?.quote_types;
    useEffect(() => {
        if (!quoteTypeOptions) return;
        const current = getValues('applies_to_ids') ?? [];
        const active = current.filter((id) => quoteTypeOptions.some((q) => q.id === id));
        if (active.length !== current.length) {
            setValue('applies_to_ids', active, { shouldValidate: true });
        }
    }, [quoteTypeOptions, getValues, setValue]);
}
